import React, { useCallback, useEffect, useState } from 'react';
import { styled, keyframes } from '@mui/system';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import { Box } from '@mui/material';

export interface AttentionBlingProps {
  id: string;
  tooltipText?: string;
}

const pulse = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.7);
  }
  
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
  }
  
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
`;

const Dot = styled('div')({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: 'black',
  animation: `${pulse} 2s infinite`,
});

const Tooltip = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: '#202020',
  color: 'white',
  borderRadius: theme.shape.borderRadius,
  marginTop: theme.spacing(1),
  whiteSpace: 'nowrap',
  fontSize: '14px',
  fontWeight: 400,
  zIndex: 1000,
  padding: '16px 32px',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-19px',
    left: '50%',
    transform: 'translateX(-50%)',
    border: '10px solid transparent',
    borderBottomColor: '#202020',
  },
}));

const viewsThresholdById: Record<string, number> = {
  '45-59-launch': 5,
};

const clicksThresholdById: Record<string, number> = {
  '45-59-launch': 3,
};

const getViewsThreshold = (id: string) => {
  if (id in viewsThresholdById) {
    return viewsThresholdById[id];
  }
  return 5;
};

const getClicksThreshold = (id: string) => {
  if (id in clicksThresholdById) {
    return clicksThresholdById[id];
  }
  return 3;
};

const AttentionBling: React.FC<AttentionBlingProps> = ({ id, tooltipText }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isTooltipVisible, setIsTooltipVisible] = useState(!!tooltipText);
  const viewsThreshold = getViewsThreshold(id);
  const clicksThreshold = getClicksThreshold(id);

  const handleBlingStats = useCallback(() => {
    if (!window.$FP) {
      return;
    }
    const { views, clicks } = window.$FP.trackBlingView(id);
    setIsVisible(views < viewsThreshold && clicks < clicksThreshold);
    setIsTooltipVisible(!!tooltipText && views < 4 && clicks === 0);
  }, [id, viewsThreshold, clicksThreshold, tooltipText]);

  const handleBlingInteraction = useCallback(
    (event: CustomEvent) => {
      if (event.detail.kind === 'click' && window.$FP) {
        const { views, clicks } = window.$FP.trackBlingClick(id);
        setIsVisible(views < viewsThreshold && clicks < clicksThreshold);
        setIsTooltipVisible(false);
      }
    },
    [id, viewsThreshold, clicksThreshold],
  );

  useEffect(() => {
    if (!window.$FP) {
      return;
    }
    document.addEventListener(
      'blingInteraction',
      handleBlingInteraction as EventListener,
    );

    handleBlingStats();

    return () => {
      document.removeEventListener(
        'blingInteraction',
        handleBlingInteraction as EventListener,
      );
    };
  }, [id, handleBlingStats, handleBlingInteraction]);

  // Hide the bling if the product line filter flag is not enabled (it's meant for 45-59 launch)
  const { productLineFilter } = useFeatureFlags();
  if (!productLineFilter) {
    return null;
  }

  if (!isVisible) {
    return null;
  }

  return (
    <Box position="relative" sx={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <Dot />
      {isTooltipVisible && tooltipText && <Tooltip>{tooltipText}</Tooltip>}
    </Box>
  );
};

export default AttentionBling;
